import { type FC } from "react"
import { Trans } from "react-i18next"
import styled from "styled-components"

import { usePlatform } from "~/contexts/PlatformContext"
import { useUser } from "~/contexts/UserContext"
import { fonts } from "~/themes/school/styles"
import { useTranslation } from "~/translations"

type Props = { certificate: { course: { title: string }; createDate: Date } }
const Certificate: FC<Props> = ({ certificate }) => {
	const platform = usePlatform().platform
	const user = useUser().user!
	const t = useTranslation()

	return (
		<Content>
			<Title>{t("certificate.certificateOfCompletion")}</Title>
			<Details>
				<Trans
					i18nKey="certificate.content"
					values={{
						name: `${user.firstName} ${user.lastName}`,
						course: certificate.course.title,
						date: new Intl.DateTimeFormat(platform.language, { dateStyle: "long" }).format(
							certificate.createDate,
						),
					}}
					components={{ value: <Value />, line: <Text /> }}
				/>
			</Details>
			{platform.logo &&
				(platform.logo.isIcon ? (
					<Icon alt="Logo" src={platform.logo.filePath} />
				) : (
					<Logo alt="Logo" src={platform.logo.filePath} />
				))}
		</Content>
	)
}

const Content = styled.div`
	width: 100%;
	background-color: white;
	border: 10px solid ${props => props.theme.primaryColor};
	padding: 64px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 48px;
`

const Title = styled.h1`
	font-size: 32px;
	font-weight: bold;
	text-transform: uppercase;
	text-align: center;
`

const Details = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
`

const Text = styled.div`
	font-size: 20px;
	text-align: center;
`

const Value = styled.div`
	font-family: ${fonts.quicksand};
	font-size: 28px;
	font-weight: 600;
	text-align: center;
`

const Icon = styled.img`
	width: 100px;
	height: 100px;
	object-fit: contain;
`

const Logo = styled.img`
	width: 500px;
	height: 50px;
	object-fit: contain;
`

export default Certificate
